import { Box } from '@mui/material';
import { Link } from 'found';
import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { AsyncActionStatus } from '../../actions/common.actionTypes';
import { customSizes } from '../../assets/styles/themes/tokens';
import { routes } from '../../constants';
import { NotificationContext } from '../../state/NotificationContextProvider';
import type { ContentItem } from '../../types/AppContent';
import useResponsivePadding from '../../utils/useResponsivePadding';
import CollectionsAppLayout from '../AppLayout/Collections/CollectionsAppLayout';
import MasterLayout from '../AppLayout/MasterLayout';
import ItemsList from '../ItemsList';
import type { Alert } from '../NotificationAlert/NotificationAlert';
import EmptySearchStateImage from '../Search/SearchResults/EmptySearchResultsNewUI/EmptySearchStateImage';

interface MyDownloadsProps {
	downloadStatus: AsyncActionStatus;
	downloadedItems: ContentItem[];
}

const MyDownloads: React.FC<MyDownloadsProps> = (props) => {
	const { downloadStatus, downloadedItems } = props;
	const { t } = useTranslation();
	const contentPadding = useResponsivePadding();
	const { showNotification } = useContext(NotificationContext);

	const handleShowNotification = (alert: Alert) => {
		showNotification(alert);
	};

	const renderMyDownloads = () => {
		if (downloadStatus !== 'loading' && (!downloadedItems || !downloadedItems.length)) {
			return (
				<EmptySearchStateImage
					title={t('no downloads yet')}
					subtitle={
						<Trans i18nKey="Start searching for the right content to download!" key={1}>
							<Link to={`/${routes.search.root}`} />
						</Trans>
					}
					subtitleSx={{
						maxWidth: customSizes.emptyStateSubtitleMaxWidth,
						textAlign: 'center',
					}}
					iconName="emptyState"
				/>
			);
		}

		return (
			<Box sx={{ position: 'relative' }} data-testid="my-downloads-list">
				<ItemsList
					isLoading={downloadStatus === 'loading'}
					hasNext={false}
					total={downloadStatus === 'loading' ? -1 : downloadedItems.length}
					loadNext={() => {}}
					items={downloadedItems}
					linkRoot={[routes.downloads.root]}
					onShowNotification={handleShowNotification}
				/>
			</Box>
		);
	};

	return (
		<Box data-testid="my-downloads-page">
			<MasterLayout isNewMerklistePage={true}>
				<CollectionsAppLayout
					title={t('Downloads')}
					subtext={downloadedItems.length.toString()}
					isSearchPageContainer={true}
				>
					<Box sx={{ padding: contentPadding }}>{renderMyDownloads()}</Box>
				</CollectionsAppLayout>
			</MasterLayout>
		</Box>
	);
};

export default MyDownloads;
